import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_typeof() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🔍 JavaScript typeof Operator</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <b>typeof</b> operator in JavaScript is used to find the type of a
        variable or expression. It returns a string describing the type of the
        operand.
      </p>

      {/* Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>typeof</code> returns a string that represents the type.
          </li>
          <li>Works for both primitive and non-primitive values.</li>
          <li>
            Known issue: <code>typeof null</code> returns <b>"object"</b>.
          </li>
          <li>
            Use <code>Array.isArray()</code> to specifically check for arrays.
          </li>
          <li>Helpful for debugging and type checking in code.</li>
        </ul>
      </div>

      {/* Example 1: Primitive Types */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ typeof with Primitive Types</h2>
        <p>
          Let’s see what <code>typeof</code> returns for basic data types.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(typeof "Hello");   // string
console.log(typeof 42);        // number
console.log(typeof true);      // boolean
console.log(typeof undefined); // undefined
console.log(typeof null);      // object
console.log(typeof 123n);      // bigint
console.log(typeof Symbol());  // symbol`}
          />
          <TryItButton
            code={`console.log(typeof "Hello");
console.log(typeof 42);
console.log(typeof true);
console.log(typeof undefined);
console.log(typeof null);
console.log(typeof 123n);
console.log(typeof Symbol());`}
          />
        </div>

        <OutputBox>
          <pre>
            string
            {"\n"}number
            {"\n"}boolean
            {"\n"}undefined
            {"\n"}object
            {"\n"}bigint
            {"\n"}symbol
          </pre>
        </OutputBox>
      </div>

      {/* Example 2: Non-Primitive Types */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ typeof with Non-Primitive Types</h2>
        <p>
          Objects, arrays, and functions all have their own results when used
          with <code>typeof</code>.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(typeof {name: "John"});  // object
console.log(typeof [1, 2, 3]);     // object
console.log(typeof function(){});  // function`}
          />
          <TryItButton
            code={`console.log(typeof {name: "John"});
console.log(typeof [1, 2, 3]);
console.log(typeof function(){});`}
          />
        </div>

        <OutputBox>
          <pre>
            object
            {"\n"}object
            {"\n"}function
          </pre>
        </OutputBox>
      </div>

      {/* Example 3: Checking Arrays Properly */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Checking Arrays Correctly</h2>
        <p>
          Since <code>typeof</code> returns "object" for arrays, use{" "}
          <code>Array.isArray()</code> to accurately detect arrays.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let arr = [10, 20, 30];
console.log(typeof arr);          // object
console.log(Array.isArray(arr));  // true`}
          />
          <TryItButton
            code={`let arr = [10, 20, 30];
console.log(typeof arr);
console.log(Array.isArray(arr));`}
          />
        </div>

        <OutputBox>
          <pre>
            object
            {"\n"}true
          </pre>
        </OutputBox>
      </div>

      {/* Example 4: typeof with Expressions */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ typeof with Expressions</h2>
        <p>
          You can use <code>typeof</code> directly on expressions to find the
          resulting type.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(typeof (10 + 5));     // number
console.log(typeof ("Hi" + "JS")); // string
console.log(typeof (true + 2));    // number`}
          />
          <TryItButton
            code={`console.log(typeof (10 + 5));
console.log(typeof ("Hi" + "JS"));
console.log(typeof (true + 2));`}
          />
        </div>

        <OutputBox>
          <pre>
            number
            {"\n"}string
            {"\n"}number
          </pre>
        </OutputBox>
      </div>

      {/* Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Use <code>typeof</code> for quick type checking.
          </li>
          <li>
            Remember that <code>typeof null</code> returns <b>"object"</b>.
          </li>
          <li>
            Use <code>Array.isArray()</code> instead of <code>typeof</code> for
            arrays.
          </li>
          <li>
            Combine <code>typeof</code> with <code>===</code> for reliable
            condition checks.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>Data Types</b> to understand how typeof behaves.
          </li>
          <li>
            Explore <b>Type Conversion</b> to change data between types.
          </li>
          <li>
            Discover <b>Operators</b> to perform logical and arithmetic
            operations.
          </li>
        </ul>
      </div>
    </div>
  );
}
